import React, { useRef, useState, useEffect } from 'react';
import { Download } from 'lucide-react';

// Import your pass template image
import passTemplate from '../../images/virtualpass.png';

const VirtualPassGenerator = () => {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const [userName, setUserName] = useState('Oluwanititemi Aderibigberikale');
    const [userId, setUserId] = useState('200134');
    const [backgroundImage, setBackgroundImage] = useState<HTMLImageElement | null>(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    // Load the pass template on mount
    useEffect(() => {
        const img = new Image();
        img.onload = () => {
            setBackgroundImage(img);
            setIsLoading(false);
            drawPass(img, userName, userId);
        };
        img.onerror = () => {
            console.error('Failed to load pass template');
            setIsLoading(false);
        };
        
        img.src = passTemplate;
        
    }, []);

    const drawPass = (bgImage: HTMLImageElement, name: string, id: string) => {
    const canvas = canvasRef.current;
    if (!canvas || !bgImage) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size to match image
    canvas.width = bgImage.width;
    canvas.height = bgImage.height;

    // Draw background image
    ctx.drawImage(bgImage, 0, 0);

    // Draw Name
    const nameX = canvas.width * 0.38;
    const nameY = canvas.height * 0.35;
    const maxWidth = canvas.width * 0.5; // max width for text area

    let nameFontSize = canvas.width * 0.055;
    ctx.font = `bold ${nameFontSize}px Creato Display, sans-serif`;

    // Reduce font size until text fits within maxWidth
    while (ctx.measureText(name).width > maxWidth && nameFontSize > 10) {
        nameFontSize -= 1;
        ctx.font = `bold ${nameFontSize}px Creato Display, sans-serif`;
    }

    ctx.fillStyle = '#FFFFFF';
    ctx.textBaseline = 'top';
    ctx.fillText(name, nameX, nameY);

    // Draw ID
    const idX = canvas.width * 0.38;
    const idY = canvas.height * 0.468;
    const idFontSize = canvas.width * 0.055;

    ctx.fillStyle = '#FFFFFF';
    ctx.font = `bold ${idFontSize}px Creato Display, sans-serif`;
    ctx.fillText(id, idX, idY);
};


    const handleDownloadPass = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        setIsGenerating(true);
    
        canvas.toBlob((blob) => {
            if (!blob) {
                setIsGenerating(false);
                return;
            }

            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.download = `haystack-pass-${userId}.png`;
            link.href = url;
            link.click();
            URL.revokeObjectURL(url);
            setIsGenerating(false);
        }, 'image/png', 1.0);
    };

    // Redraw when name or ID changes
    useEffect(() => {
        if (backgroundImage) {
            drawPass(backgroundImage, userName, userId);
        }
    }, [userName, userId, backgroundImage]);

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gray-900 flex items-center justify-center">
                <div className="text-white text-xl">Loading pass</div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-900 p-8 flex flex-col items-center gap-8">
            {/* Controls */}
            <div className="bg-gray-800 p-6 rounded-xl w-full max-w-md space-y-4">
                <h2 className="text-white text-xl font-semibold mb-4">Virtual Pass Generator</h2>

                {/* Name Input */}
                <div>
                    <label className="block text-white text-sm mb-2">Full Name</label>
                    <input
                        type="text"
                        value={userName}
                        onChange={(e) => setUserName(e.target.value)}
                        placeholder="Enter full name"
                        className="w-full px-4 py-2 rounded-lg bg-gray-700 text-white outline-none"
                    />
                </div>

                {/* ID Input */}
                <div>
                    <label className="block text-white text-sm mb-2">ID Number</label>
                    <input
                        type="text"
                        value={userId}
                        onChange={(e) => setUserId(e.target.value)}
                        placeholder="Enter ID"
                        className="w-full px-4 py-2 rounded-lg bg-gray-700 text-white outline-none"
                    />
                </div>

                {/* Download Button */}
                <button
                    onClick={handleDownloadPass}
                    disabled={!backgroundImage || isGenerating}
                    className="w-full bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    <Download size={20} />
                    {isGenerating ? 'Generating...' : 'Download Pass'}
                </button>
            </div>

            {/* Canvas Preview */}
            <div className="bg-gray-800 p-6 rounded-xl">
                <h3 className="text-white text-lg mb-4">Preview</h3>
                <canvas
                    ref={canvasRef}
                    className="max-w-sm h-auto rounded-lg shadow-2xl"
                />
            </div>

        </div>
    );
};

export default VirtualPassGenerator;